
import React, { useState, useEffect, useCallback } from "react";
import { Customer } from "@/api/entities/Customer";
import { Case } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit, Mail, Phone, Globe, Calendar, Briefcase, Plus, MoreVertical, MapPin, Languages } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import { safeFormat } from '@/lib/safeDate';
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import ContactForm from "../components/contacts/ContactForm";

const statusColors = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  inactive: "bg-slate-100 text-slate-700 border-slate-200",
  completed: "bg-blue-100 text-blue-700 border-blue-200"
};

const caseStatusColors = {
  preparation: "bg-blue-100 text-blue-700",
  document_collection: "bg-cyan-100 text-cyan-700",
  petition_filed: "bg-indigo-100 text-indigo-700",
  rfe_response: "bg-amber-100 text-amber-700",
  biometrics: "bg-purple-100 text-purple-700",
  interview: "bg-orange-100 text-orange-700",
  adjudication: "bg-yellow-100 text-yellow-700",
  approved: "bg-emerald-100 text-emerald-700",
  denied: "bg-rose-100 text-rose-700",
  withdrawn: "bg-slate-100 text-slate-700",
  closed: "bg-slate-100 text-slate-700"
};

export default function CustomerDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const loadData = useCallback(async () => {
    if (!location.pathname.includes('CustomerDetail')) {
      return;
    }

    setIsLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const customerId = urlParams.get('id');

    if (!customerId) {
      navigate(createPageUrl("Customers"));
      return;
    }

    try {
      const customersData = await Customer.list();
      const foundCustomer = customersData.find(c => c.id === customerId);
      
      if (!foundCustomer) {
        navigate(createPageUrl("Customers"));
        return;
      }

      setCustomer(foundCustomer);

      const casesData = await Case.list("-created_date");
      const customerCases = casesData.filter(c => c.customer_id === customerId);
      setCases(customerCases);
    } catch (error) {
      console.error("Error loading customer data:", error);
    }

    setIsLoading(false);
  }, [navigate, location.pathname]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdateCustomer = async (updatedCustomerData) => {
    setIsProcessing(true);
    try {
      await Customer.update(customer.id, updatedCustomerData);
      setIsEditing(false);
      loadData();
    } catch (error) {
      console.error("Error updating customer:", error);
    }
    setIsProcessing(false);
  };

  const handleViewCase = (caseId) => {
    navigate(createPageUrl("CaseDetail") + "?id=" + caseId);
  };

  const handleCreateCase = () => {
    navigate(createPageUrl("Cases") + "?customer_id=" + customer.id);
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 md:p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="outline"
            onClick={() => setIsEditing(false)}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <ContactForm
            customer={customer}
            onSubmit={handleUpdateCustomer}
            onCancel={() => setIsEditing(false)}
            isProcessing={isProcessing}
          />
        </div>
      </div>
    );
  }

  const getFullName = () => {
    if (customer.first_name && customer.last_name) {
      return `${customer.first_name} ${customer.last_name}`;
    }
    return customer.full_name || 'Unknown Customer';
  };

  const getInitials = () => {
    if (customer.first_name && customer.last_name) {
      return `${customer.first_name.charAt(0)}${customer.last_name.charAt(0)}`.toUpperCase();
    }
    if (customer.full_name) {
      return customer.full_name.charAt(0).toUpperCase();
    }
    return 'C';
  };

  return (
    <div className="bg-white p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(createPageUrl("Customers"))}
          className="mb-6 -ml-2 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Customers
        </button>

        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-2xl">
                {getInitials()}
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">{getFullName()}</h1>
              <Badge className={`${statusColors[customer.status]} border font-medium pointer-events-none`}>
                {customer.status}
              </Badge>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-slate-600 hover:text-slate-600 hover:bg-transparent transition-all duration-200 hover:scale-105"
              >
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Customer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div>
          <div className="flex gap-2 mb-8 bg-slate-100 p-1.5 rounded-full inline-flex overflow-x-auto">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full whitespace-nowrap ${
                activeTab === "overview"
                  ? "text-slate-900 bg-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("cases")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full flex items-center gap-2 whitespace-nowrap ${
                activeTab === "cases"
                  ? "text-slate-900 bg-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Cases
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === "cases" 
                  ? "bg-blue-100 text-blue-600" 
                  : "bg-slate-200 text-slate-600"
              }`}>
                {cases.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("service")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full whitespace-nowrap ${
                activeTab === "service"
                  ? "text-slate-900 bg-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Service
            </button>
            <button
              onClick={() => setActiveTab("finance")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full whitespace-nowrap ${
                activeTab === "finance"
                  ? "text-slate-900 bg-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Finance
            </button>
          </div>

          {activeTab === "overview" && (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content - Left Side */}
              <div className="lg:col-span-2 space-y-8">
                {/* Customer Information */}
                <div>
                  <h2 className="text-lg font-bold text-slate-900 mb-6">Customer Information</h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Contact Details */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Contact</h3>
                      {customer.email && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <Mail className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500">Email</p>
                            <p className="break-all font-medium">{customer.email}</p>
                          </div>
                        </div>
                      )}
                      {customer.primary_phone && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <Phone className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500">Primary Phone</p>
                            <p className="font-medium">{customer.primary_phone}</p>
                          </div>
                        </div>
                      )}
                      {customer.additional_phones && customer.additional_phones.length > 0 && (
                        <div className="pt-2">
                          <p className="text-xs text-slate-500 mb-2">Additional Phones</p>
                          <div className="space-y-2">
                            {customer.additional_phones.map((phone, index) => (
                              <div key={index} className="flex items-center gap-2 text-sm text-slate-700">
                                <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <span>{phone.phone}</span>
                                {phone.label && (
                                  <Badge variant="outline" className="text-xs">{phone.label}</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Personal Details */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Personal</h3>
                      {customer.date_of_birth && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <Calendar className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500">Date of Birth</p>
                            <p className="font-medium">{safeFormat(new Date(customer.date_of_birth), "MMMM d, yyyy")}</p>
                          </div>
                        </div>
                      )}
                      {customer.country_of_birth && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <Globe className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500">Nationality</p>
                            <p className="font-medium">{customer.country_of_birth}</p>
                          </div>
                        </div>
                      )}
                      {customer.preferred_language && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <Languages className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500">Preferred Language</p>
                            <p className="font-medium">{customer.preferred_language}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                {customer.primary_address && (
                  <div className="pt-8 border-t border-slate-100">
                    <h2 className="text-lg font-bold text-slate-900 mb-6">Address</h2>
                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Primary Address */}
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Primary</h3>
                        <div className="flex items-start gap-3 text-slate-700">
                          <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          <div>
                            {customer.primary_address.street && <p className="font-medium">{customer.primary_address.street}</p>}
                            {customer.primary_address.line2 && <p className="text-sm text-slate-600">{customer.primary_address.line2}</p>}
                            <p className="text-sm text-slate-600">
                              {[customer.primary_address.city, customer.primary_address.state, customer.primary_address.zip]
                                .filter(Boolean)
                                .join(', ')}
                            </p>
                            {customer.primary_address.country && <p className="text-sm text-slate-600">{customer.primary_address.country}</p>}
                          </div>
                        </div>
                      </div>

                      {/* Additional Addresses */}
                      {customer.additional_addresses && customer.additional_addresses.map((addr, index) => (
                        <div key={index} className="space-y-4">
                          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                            {addr.label || 'Additional'}
                          </h3>
                          <div className="flex items-start gap-3 text-slate-700">
                            <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                            <div>
                              {addr.street && <p className="font-medium">{addr.street}</p>}
                              {addr.line2 && <p className="text-sm text-slate-600">{addr.line2}</p>}
                              <p className="text-sm text-slate-600">
                                {[addr.city, addr.state, addr.zip]
                                  .filter(Boolean)
                                  .join(', ')}
                              </p>
                              {addr.country && <p className="text-sm text-slate-600">{addr.country}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar - Right Side */}
              <div className="space-y-8">
                {/* Quick Stats */}
                <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4">Quick Overview</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-slate-500">Status</p>
                      <Badge className={`${statusColors[customer.status]} border font-medium mt-1`}>
                        {customer.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Customer Since</p>
                      <p className="text-slate-900 font-medium mt-1">
                        {safeFormat(new Date(customer.created_date), "MMMM d, yyyy")}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Total Cases</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{cases.length}</p>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {customer.notes && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Notas</h3>
                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg">
                      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{customer.notes}</p>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <Button
                      onClick={handleCreateCase}
                      className="w-full bg-blue-600 hover:bg-blue-700 justify-start"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create New Case
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      className="w-full justify-start border-slate-300"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Information
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "cases" && (
            <div>
              <div className="mb-6 flex justify-end">
                <Button
                  onClick={handleCreateCase}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Case
                </Button>
              </div>

              {cases.length > 0 ? (
                <div className="space-y-4">
                  {cases.map((caseItem) => (
                    <Card
                      key={caseItem.id}
                      onClick={() => handleViewCase(caseItem.id)}
                      className="border-slate-200 hover:shadow-lg hover:scale-[1.01] transition-all duration-300 cursor-pointer"
                    >
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-slate-900 mb-1">
                              {caseItem.case_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </h3>
                            {caseItem.case_number && (
                              <p className="text-sm text-slate-500">Case #{caseItem.case_number}</p>
                            )}
                          </div>
                          <Badge className={`${caseStatusColors[caseItem.status]} border-0 font-medium pointer-events-none`}>
                            {caseItem.status.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                          {caseItem.filing_date && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Filed: {safeFormat(new Date(caseItem.filing_date), "MMM d, yyyy")}</span>
                            </div>
                          )}
                          {caseItem.next_hearing_date && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-blue-600" />
                              <span className="text-blue-600">Hearing: {safeFormat(new Date(caseItem.next_hearing_date), "MMM d, yyyy")}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500 font-medium mb-2">No cases yet</p>
                    <p className="text-sm text-slate-400 mb-4">
                      Create the first case for this customer
                    </p>
                    <Button
                      onClick={handleCreateCase}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Case
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === "service" && (
            <div className="max-w-4xl">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Contratos de Serviço</h2>
                <p className="text-slate-600">Gerencie pacotes de serviços e contratos deste cliente</p>
              </div>

              <Card className="border-slate-200 shadow-sm mb-6">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum Contrato de Serviço</h3>
                  <p className="text-slate-500 mb-6">
                    Crie um contrato de serviço para começar
                  </p>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Contrato de Serviço
                  </Button>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-4">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold text-slate-900">Serviços Ativos</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="text-center text-slate-400">
                      <p className="text-3xl font-bold text-slate-900 mb-1">0</p>
                      <p className="text-sm">Pacotes de serviço ativos</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold text-slate-900">Serviços Concluídos</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="text-center text-slate-400">
                      <p className="text-3xl font-bold text-slate-900 mb-1">0</p>
                      <p className="text-sm">Serviços concluídos</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === "finance" && (
            <div className="max-w-4xl">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Visão Financeira</h2>
                <p className="text-slate-600">Acompanhe pagamentos, faturas e registros financeiros</p>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold text-slate-600">Total Revenue</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <p className="text-3xl font-bold text-slate-900">$0.00</p>
                  </CardContent>
                </Card>

                <Card className="border-emerald-200 shadow-sm bg-emerald-50">
                  <CardHeader className="border-b border-emerald-100">
                    <CardTitle className="text-sm font-semibold text-emerald-700">Amount Paid</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <p className="text-3xl font-bold text-emerald-700">$0.00</p>
                  </CardContent>
                </Card>

                <Card className="border-amber-200 shadow-sm bg-amber-50">
                  <CardHeader className="border-b border-amber-100">
                    <CardTitle className="text-sm font-semibold text-amber-700">Balance Due</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <p className="text-3xl font-bold text-amber-700">$0.00</p>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-slate-900">Payment History</CardTitle>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Record Payment
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">No payment history yet</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-slate-900">Faturas</CardTitle>
                      <Button size="sm" variant="outline" className="border-slate-300">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Invoice
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">No invoices generated yet</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
